<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useFeedback } from '../composables/useFeedback';
import { type AutoReviewType, isAutoReviewable, useAutoReview, useReviews } from '../composables/useReviews';
import { reviewNavItems } from '../nav';
import { errorDetail } from '../utils/error';
import {
  formatDaysAgo,
  formatReviewedOnDate,
  minimumDaysByFilter,
  type FreshnessFilter,
} from '../utils/reviewFreshness';
import { groupFreshness, toReviewRow, type ReviewRow } from '../utils/reviewRows';
import type { PostReviewsByReviewTypeAutoResponse, ReviewType } from '../api';

const props = defineProps<{ reviewType: ReviewType }>();

const reviewType = computed(() => props.reviewType);
const label = computed(() => reviewNavItems.find((item) => item.reviewType === props.reviewType)?.label ?? 'Reviews');

const { notify } = useFeedback();
const canAutoReview = computed(() => isAutoReviewable(reviewType.value));
// Only called from the button below, which canAutoReview guards, so this always resolves to a
// real aws/github value by the time a request actually goes out.
const autoReview = useAutoReview(computed(() => reviewType.value as AutoReviewType));

// Dry-run result awaiting confirmation in the dialog below; null means the dialog is closed.
const autoReviewPreview = ref<PostReviewsByReviewTypeAutoResponse | null>(null);
const autoReviewPreviewRows = computed(() => (autoReviewPreview.value ?? []).map(toReviewRow));
const autoReviewDialogTitle = computed(
  () =>
    `Auto-approve ${autoReviewPreviewRows.value.length} empty group${autoReviewPreviewRows.value.length === 1 ? '' : 's'}?`
);

function runAutoReview() {
  autoReview.mutate(true, {
    onSuccess: (preview) => {
      const rows = preview ?? [];
      if (!rows.length) {
        notify({ tone: 'info', summary: 'Nothing to auto-approve right now.', life: 20_000 });
        return;
      }
      autoReviewPreview.value = rows;
    },
    onError: (err) => notify({ tone: 'error', summary: 'Failed to preview auto-approval', detail: errorDetail(err) }),
  });
}

function confirmAutoReview() {
  const count = autoReviewPreviewRows.value.length;
  autoReview.mutate(false, {
    onSuccess: () => {
      autoReviewPreview.value = null;
      notify({ tone: 'success', summary: `Auto-approved ${count} empty group${count === 1 ? '' : 's'}` });
    },
    onError: (err) => notify({ tone: 'error', summary: 'Failed to auto-approve', detail: errorDetail(err) }),
  });
}

// The table is table-layout: fixed (see <style>), so these widths hold whatever the content, and
// the group header's colspan cells line up with the columns below them. Name and Reviewer split
// what's left.
const columns = [
  { title: 'Name', key: 'name' },
  { title: 'Reviewer', key: 'reviewer' },
  { title: 'Reviewed on', key: 'reviewedOn', width: '9.5rem', cellProps: { class: 'pr-0' } },
  {
    title: '',
    key: 'daysAgo',
    sortable: false,
    width: '9rem',
    headerProps: { class: 'pl-0' },
    cellProps: { class: 'pl-0' },
  },
];

const thresholds = computed(() => minimumDaysByFilter(reviewType.value));
const freshnessFilters = computed<
  { value: FreshnessFilter; label: string; color?: 'warning' | 'error'; icon?: string }[]
>(() => [
  { value: 'all', label: 'All' },
  { value: 'due', label: `${thresholds.value.due}+ days`, color: 'warning', icon: 'warning' },
  { value: 'overdue', label: `${thresholds.value.overdue}+ days`, color: 'error', icon: 'error' },
]);

// Overdue items are the ones that need attention, so that's the default view rather than
// everything.
const freshnessFilter = ref<FreshnessFilter>('due');
const minimumDays = computed(() => thresholds.value[freshnessFilter.value]);

const route = useRoute();
const router = useRouter();

// URL wins over the remembered value, which wins over ''. Same layering as ally's
// usePersistedFilter, but free text has no validValues to revalidate against, so this is the
// lighter version: just resolve-then-mirror, no stale-value check.
function searchStorageKey(type: ReviewType): string {
  return `review-search:${type}`;
}

const search = ref('');
// Debounced so typing doesn't fire a request per keystroke; the search box itself still updates
// (and mirrors to the URL/storage below) immediately.
const debouncedSearch = ref('');

// Resolves on mount and on every navigation (switching tabs, browser back/forward, editing the
// URL by hand): reviewType changing is what makes each view's remembered search its own.
watch(
  [() => route.query.search, reviewType],
  ([queryValue, type]) => {
    const fromQuery = typeof queryValue === 'string' ? queryValue : null;
    const resolved = fromQuery ?? localStorage.getItem(searchStorageKey(type)) ?? '';
    // Typing's own URL mirror echoes back here; leave that to the debounce below.
    if (resolved === search.value) return;
    search.value = resolved;
    // Not debounced: a restored term should go out with the first request, not 300ms after it.
    debouncedSearch.value = resolved;
  },
  { immediate: true }
);

// Mirrors search into both the URL (shareable/bookmarkable) and localStorage (remembered per
// view for next time), whatever changed it, typing or the resolver above restoring a stored
// value. router.replace (not push) so typing doesn't spam browser history; skipped when the URL
// already agrees, so restoring from storage doesn't loop back through the resolver above.
watch(search, (value) => {
  const key = searchStorageKey(reviewType.value);
  if (value) {
    localStorage.setItem(key, value);
  } else {
    localStorage.removeItem(key);
  }

  const current = typeof route.query.search === 'string' ? route.query.search : '';
  if (value === current) return;
  const query = { ...route.query };
  if (value) {
    query.search = value;
  } else {
    delete query.search;
  }
  void router.replace({ query });
});

let searchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
watch(search, (value) => {
  clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    debouncedSearch.value = value;
  }, 300);
});

const itemsPerPageOptions = [25, 50, 100];
const itemsPerPage = ref(50);
const page = ref(1);
const sortBy = ref<{ key: string; order?: boolean | 'asc' | 'desc' }[]>([]);

// Name sorts by group name; every other column sorts across all entries. Unsorted, rows are kept
// grouped so a group's rows stay contiguous across pages.
const sort = computed(() => {
  if (!sortBy.value.length) return 'group';
  return sortBy.value
    .map(({ key, order }) => {
      const apiKey = key === 'name' ? 'group' : key;
      return order === 'desc' ? `-${apiKey}` : apiKey;
    })
    .join(',');
});

const {
  data: reviewsPage,
  isFetching,
  error,
  refetch,
} = useReviews(reviewType, minimumDays, debouncedSearch, page, itemsPerPage, sort);
const rows = computed(() => (reviewsPage.value?.items ?? []).map(toReviewRow));
const totalCount = computed(() => reviewsPage.value?.totalCount ?? 0);

// The result set changes whenever reviewType, the freshness filter, the page size, the search
// term or the sort does, so whatever page the reviewer was on no longer means the same thing.
watch([reviewType, minimumDays, itemsPerPage, debouncedSearch, sort], () => {
  page.value = 1;
});

// Group by the backend's group field, but only once any row has one; ungrouped rows then land in
// a '' group of their own. The group column is declared here (rather than left to Vuetify) to keep
// it narrow and unsortable.
const isGrouped = computed(() => rows.value.some((row) => row.group));
const groupBy = computed(() => (isGrouped.value ? [{ key: 'group', order: 'asc' as const }] : []));
const headers = computed(() =>
  isGrouped.value ? [{ title: '', key: 'data-table-group', sortable: false, width: '3rem' }, ...columns] : columns
);

// Each group header's rolled-up status, over the rows currently shown, computed once per group
// rather than per template binding.
const groupStatuses = computed(() => {
  const byGroup = new Map<string, ReviewRow[]>();
  for (const row of rows.value) {
    const groupRows = byGroup.get(row.group) ?? [];
    groupRows.push(row);
    byGroup.set(row.group, groupRows);
  }
  return new Map(
    [...byGroup].map(([group, groupRows]) => {
      const { worst, overdue, due } = groupFreshness(groupRows);
      const parts = [overdue && `${overdue} overdue`, due && `${due} due`].filter(Boolean);
      return [group, { icon: worst.icon, color: worst.color, text: parts.join(', ') || 'All recently reviewed' }];
    })
  );
});

const selected = ref<string[]>([]);

// Managed directly (rather than the table's open-all prop, which only ever forces groups open:
// its own watcher no-ops as soon as open-all goes false, so it can force-open but never close).
// group-key set to the raw group value, so these ids are just that value, not whatever internal
// format Vuetify happens to generate them in.
const openedGroups = ref<string[]>([]);
const allGroupIds = computed(() => [...groupStatuses.value.keys()]);
const allGroupsOpen = computed(
  () => allGroupIds.value.length > 0 && allGroupIds.value.every((id) => openedGroups.value.includes(id))
);

function toggleAllGroups() {
  openedGroups.value = allGroupsOpen.value ? [] : allGroupIds.value;
}

function groupKey({ value }: { value: unknown }): string {
  return String(value);
}

// Each page shows a different set of groups, so a previous page's open/closed state doesn't mean
// anything once the page changes.
watch(page, () => {
  openedGroups.value = [];
});

// ReviewListView is reused across reviewType changes (same route record, only :reviewType
// differs), so without this a selection or filter made on one type's table would otherwise
// bleed into the next tab, most seriously as stale ids in startReview()'s review-stack link.
watch(reviewType, () => {
  selected.value = [];
  freshnessFilter.value = 'due';
  autoReviewPreview.value = null;
  openedGroups.value = [];
});

function startReview() {
  void router.push({
    name: 'review-stack',
    params: { reviewType: reviewType.value },
    query: { ids: selected.value.join(',') },
  });
}
</script>

<template>
  <div class="review-list">
    <div class="d-flex align-center justify-space-between">
      <h1>{{ label }}</h1>
      <div class="d-flex ga-2">
        <v-btn
          v-if="canAutoReview"
          variant="outlined"
          :loading="autoReview.isPending.value"
          text="Auto-approve empty groups"
          @click="runAutoReview"
        />
        <v-btn :disabled="!selected.length" text="Start review" @click="startReview" />
      </div>
    </div>

    <div class="d-flex align-center flex-wrap ga-4 mt-4">
      <v-btn
        v-if="isGrouped"
        variant="text"
        density="compact"
        :prepend-icon="allGroupsOpen ? 'unfold_less' : 'unfold_more'"
        :text="allGroupsOpen ? 'Collapse all' : 'Expand all'"
        @click="toggleAllGroups"
      />
      <v-spacer />
      <v-btn-toggle v-model="freshnessFilter" mandatory density="compact" variant="outlined" divided>
        <v-btn v-for="option in freshnessFilters" :key="option.value" :value="option.value">
          <v-icon v-if="option.icon" :icon="option.icon" size="x-small" :color="option.color" class="mr-2" />
          {{ option.label }}
        </v-btn>
      </v-btn-toggle>
      <v-text-field
        v-model="search"
        placeholder="Search name or reviewer…"
        prepend-inner-icon="search"
        clearable
        hide-details
        density="compact"
        style="max-inline-size: 20rem"
      />
    </div>

    <!-- A plain v-alert here would stretch to fill the space the table would've taken (Vuetify
         gives it flex: 1 1 by default); a centered card of its own natural size reads better. -->
    <div v-if="error" class="review-error">
      <v-card max-width="28rem">
        <v-card-item>
          <template #prepend><v-icon icon="error" color="error" size="36" /></template>
          <v-card-title>Couldn't load reviews</v-card-title>
        </v-card-item>
        <v-card-text>{{ errorDetail(error) }}</v-card-text>
        <v-card-actions>
          <v-btn variant="outlined" color="error" prepend-icon="refresh" @click="refetch()">Retry</v-btn>
        </v-card-actions>
      </v-card>
    </div>
    <v-data-table-server
      v-else
      v-model="selected"
      v-model:opened="openedGroups"
      v-model:page="page"
      v-model:items-per-page="itemsPerPage"
      v-model:sort-by="sortBy"
      :items-per-page-options="itemsPerPageOptions"
      :items-length="totalCount"
      :loading="isFetching"
      :items="rows"
      :headers="headers"
      :group-by="groupBy"
      :group-key="groupKey"
      item-value="id"
      item-selectable="canReview"
      show-select
      fixed-header
      height="100%"
      density="compact"
      class="review-table mt-4"
    >
      <!-- Replaces only the group cell: Vuetify's own group header still renders the checkbox,
           which selects just the group's reviewable rows and disables itself when there are none.
           The name cell spans up to the date column (every column but select and the last two),
           so the status chip starts where the dates below it do. -->
      <template #data-table-group="{ item: group, count, props: toggle }">
        <td :colspan="headers.length - 2">
          <div class="d-flex align-center ga-2">
            <v-btn size="small" variant="text" v-bind="toggle" />
            <span class="font-weight-medium">{{ group.value || 'Ungrouped' }}</span>
            <span class="text-medium-emphasis">({{ count }})</span>
          </div>
        </td>
        <td colspan="2">
          <v-chip
            size="small"
            :color="groupStatuses.get(group.value)?.color"
            :prepend-icon="groupStatuses.get(group.value)?.icon"
            :text="groupStatuses.get(group.value)?.text"
          />
        </td>
      </template>
      <template #item.name="{ item }">
        <RouterLink :to="{ name: 'review-detail', params: { reviewType, id: item.id } }">{{ item.name }}</RouterLink>
      </template>
      <template #item.reviewedOn="{ item }">
        <!-- ps-1 matches the small group chip's icon inset (10px padding, -5px icon margin). -->
        <span class="d-flex align-center ga-2 ps-1">
          <v-icon
            :icon="item.freshness.icon"
            size="x-small"
            :color="item.freshness.color"
            :title="item.freshness.label"
          />
          {{ formatReviewedOnDate(item.reviewedOn) }}
        </span>
      </template>
      <template #item.daysAgo="{ item }">
        <span class="text-medium-emphasis text-no-wrap">{{ formatDaysAgo(item.freshness.days) }}</span>
      </template>
      <template #no-data>Nothing to review.</template>
    </v-data-table-server>

    <v-dialog
      :model-value="autoReviewPreview !== null"
      max-width="56rem"
      @update:model-value="autoReviewPreview = null"
    >
      <v-card :title="autoReviewDialogTitle">
        <v-card-text>
          <!-- A real table, not a list faking one: same Name / Reviewer / Reviewed on columns as
               the table above, in the same order, so values line up the way its columns do
               instead of wrapping per-row like a v-list's title/subtitle would. -->
          <v-table density="compact" fixed-header height="20rem" class="bg-surface-variant rounded">
            <thead>
              <tr>
                <th>Name</th>
                <th>Reviewer</th>
                <th>Reviewed on</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in autoReviewPreviewRows" :key="row.id">
                <td>{{ row.name }}</td>
                <td>{{ row.reviewer }}</td>
                <td>
                  <span class="d-flex align-center ga-2 text-no-wrap">
                    <v-icon
                      :icon="row.freshness.icon"
                      size="x-small"
                      :color="row.freshness.color"
                      :title="row.freshness.label"
                    />
                    {{ formatReviewedOnDate(row.reviewedOn) }}
                    <span class="text-medium-emphasis">{{ formatDaysAgo(row.freshness.days) }}</span>
                  </span>
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text="Cancel" variant="text" @click="autoReviewPreview = null" />
          <v-btn
            :loading="autoReview.isPending.value"
            color="primary"
            variant="flat"
            text="Approve"
            @click="confirmAutoReview"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
/* Fills whatever height AppFrame's content region gives this view, and clips at that bound
   (overflow: hidden), so the table's own scroll area is what scrolls, not the whole page. Matches
   ally-configuration-ui's OverviewTableLayout recipe: h-full + min-h-0 + overflow-hidden. */
.review-list {
  display: flex;
  flex-direction: column;
  block-size: 100%;
  min-block-size: 0;
  overflow: hidden;
}

.review-error {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  min-block-size: 0;
  margin-block-start: 1rem;
}

.review-table {
  flex: 1 1 auto;
  min-block-size: 0;
}

.review-table :deep(table) {
  table-layout: fixed;
}
</style>
